import { useMutation } from '@tanstack/react-query'
import { Download, FileText } from 'lucide-react'
import { useEffect, useState, type ComponentProps, type ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { saveBlobAsFile } from '@/lib/download'
import { resolveErrorMessage } from '@/lib/error-messages'

/** What a viewer needs: something that resolves to the PDF's bytes and its file name. */
export type DocumentLoader = () => Promise<{ blob: Blob; filename: string }>

interface DocumentViewerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  load: DocumentLoader
}

/**
 * The in-app PDF viewer.
 *
 * The institution's counters run the browser in kiosk mode, where a download is a file nobody can
 * open — it lands in a folder the kiosk never shows. So every generated document opens here, in a
 * dialog, rendered by the browser's own PDF viewer from a blob the application already fetched
 * through the authenticated client. "Save a copy" is still offered, for the desks that are not
 * kiosks, and hands the browser the same bytes rather than fetching again.
 *
 * The object URL is created when the bytes arrive and revoked when the dialog closes; nothing here
 * is a link into the API, so the viewer can never be bookmarked around the bearer token.
 */
export function DocumentViewerDialog({ open, onOpenChange, title, description, load }: DocumentViewerDialogProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)

  const fetchDocument = useMutation({
    mutationFn: load,
    onSuccess: ({ blob }) => {
      setObjectUrl((previous) => {
        if (previous) {
          URL.revokeObjectURL(previous)
        }
        return URL.createObjectURL(blob)
      })
    },
  })

  // Fetch on open, and forget everything on close so a re-open shows the current copy.
  useEffect(() => {
    if (open) {
      fetchDocument.mutate()
      return
    }

    setObjectUrl((previous) => {
      if (previous) {
        URL.revokeObjectURL(previous)
      }
      return null
    })
    fetchDocument.reset()
    // The mutation object is stable for the component's life; re-running on its identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const file = fetchDocument.data

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-[min(96vw,64rem)] max-w-none flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description ??
              'Shown here rather than downloaded, so a counter in kiosk mode can read it and print it.'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="min-h-0 flex-1">
          {fetchDocument.isPending ? (
            <div className="flex h-[60vh] items-center justify-center rounded-control border border-border-subtle bg-surface-muted text-sm text-muted">
              Preparing the document…
            </div>
          ) : null}

          {fetchDocument.isError ? (
            <div
              role="alert"
              className="rounded-control border border-danger-border bg-danger-subtle p-4 text-sm text-danger-text"
            >
              <p className="font-medium">The document could not be prepared.</p>
              <p className="mt-1">{resolveErrorMessage(fetchDocument.error)}</p>
            </div>
          ) : null}

          {objectUrl && file ? (
            <object
              data={objectUrl}
              type="application/pdf"
              aria-label={file.filename}
              className="h-[70vh] w-full rounded-control border border-border-subtle bg-surface-muted"
            >
              {/* The browser has no inline PDF viewer (some mobile browsers). The bytes are still here. */}
              <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center text-sm text-body">
                <FileText className="size-8 text-muted" />
                <p>This browser cannot show a PDF inline. Save a copy to open it in another app.</p>
              </div>
            </object>
          ) : null}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Done
          </Button>
          <Button
            variant="secondary"
            disabled={!file}
            onClick={() => {
              if (file) {
                saveBlobAsFile(file.blob, file.filename)
              }
            }}
          >
            <Download />
            Save a copy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface ViewDocumentButtonProps extends Omit<ComponentProps<typeof Button>, 'onClick' | 'children'> {
  /** The dialog's title — what the person is about to read, e.g. "Receipt 001-20260905-000123". */
  title: string
  description?: string
  load: DocumentLoader
  children: ReactNode
}

/**
 * A button that opens a document in the viewer. Owns its own dialog so a screen can drop one in
 * wherever it used to offer a download, without threading state through the page.
 */
export function ViewDocumentButton({ title, description, load, children, ...buttonProps }: ViewDocumentButtonProps) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button {...buttonProps} onClick={() => setOpen(true)}>
        {children}
      </Button>
      <DocumentViewerDialog open={open} onOpenChange={setOpen} title={title} description={description} load={load} />
    </>
  )
}
